import { DraggableProvided } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { ReactElement, useLayoutEffect, useRef } from 'react';
import { IGColumnProps } from './GTable';

type TableCellProps<T> = {
  column: IGColumnProps<T>;
  item: T;
  index: number;
  isDragOccurring: boolean;
  provided: DraggableProvided;
};

export interface IGSubRowModel {
  id: string;
}

export interface IGRowModel<D extends IGSubRowModel = IGSubRowModel> {
  id: string;
  subRows?: D[];
}

export const TableCell = <T extends IGRowModel<D>, D extends IGSubRowModel = IGSubRowModel>(props: TableCellProps<T>): ReactElement => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const widthRef = useRef<number | undefined>(undefined);
  const heightRef = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!cellRef.current) {
      return;
    }

    if (!props.isDragOccurring) {
      const bbox = cellRef.current.getBoundingClientRect();
      widthRef.current = bbox.width;
      heightRef.current = bbox.height;

      // inline styles not applied
      if (!cellRef.current.style.width) {
        return;
      }

      cellRef.current.style.removeProperty('height');
      cellRef.current.style.removeProperty('width');
    } else if (cellRef.current.style.width !== widthRef.current?.toString()) {
      cellRef.current.style.width = `${widthRef.current}px`;
      cellRef.current.style.height = `${heightRef.current}px`;
    }
  }, [props.isDragOccurring]);

  const { column: c, item, index, provided } = props;

  return (
    <td
      ref={cellRef}
      key={`${c.headerName}-${index}`}
      className={classNames('g-cell', c.cellClassName, { sticky: c.sticky, actions: c.actions, 'g-drag-handle': c.isDragHandle })}
      {...(c.isDragHandle ? provided.dragHandleProps : {})}
    >
      <div className='g-cell-content'>{c.renderer(item)}</div>
    </td>
  );
};
